/**
 * HasPermission.tsx
 *
 * Wrapper component that conditionally renders children based on feature permissions.
 * Reads from the Zustand permission store — no API calls, no useEffects.
 *
 * Feature codes MUST have 4 segments: blx:{route}:{feature}:{action}
 * (e.g., "blx:cms-local:Posts:view", "blx:report-viewer:Leads:manage").
 *
 * Usage:
 *   <HasPermission feature="blx:cms-local:Posts:view">
 *     <AdminPanel />
 *   </HasPermission>
 *
 *   <HasPermission feature="blx:website-builder:Pages:manage" companyId={companyId}>
 *     <WebsiteBuilder />
 *   </HasPermission>
 */

import { ReactNode } from 'react';
import { usePermission } from './usePermission';

/** Props for the HasPermission wrapper component. */
export interface HasPermissionProps {
  /** Feature code to check — MUST have 4 segments: blx:{route}:{feature}:{action} (e.g., "blx:cms-local:Posts:view"). */
  feature?: string;

  /** Optional company ID — if provided, checks company-level permission instead of global. */
  companyId?: string | null;

  /** React node rendered when the user lacks the required permission (default: null). */
  fallback?: ReactNode;

  /** Children to render when the user has the required permission. */
  children: ReactNode;

  /** Optional boolean permission flag to directly allow or deny rendering. */
  hasPermission?: boolean;
}

/**
 * Conditional rendering wrapper for feature-level permissions.
 *
 * When companyId is provided, checks the per-company feature set.
 * Otherwise falls back to global check (across all companies).
 *
 * If no features are loaded yet (permissionsLoaded === false), renders fallback.
 */
export const HasPermission = ({
  feature,
  companyId,
  fallback = null,
  children,
  hasPermission,
}: HasPermissionProps): JSX.Element | null => {
  const { hasFeature, canManage, permissionsLoaded } = usePermission();

  // If permission data hasn't loaded yet, show nothing (loading state).
  // This prevents flash-of-wrong-content when permissions haven't arrived.
  if (!permissionsLoaded) return <>{fallback}</>;

  let allowed = false;

  if (feature) {
    if (companyId) {
      allowed = canManage({ companyId, featureCode: feature });
    } else {
      allowed = hasFeature(feature);
    }
  }
  if (typeof hasPermission === 'boolean') {
    allowed = hasPermission;
  }

  return allowed ? <>{children}</> : <>{fallback}</>;
};
